import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './EloRanking.module.css';
import { fetchEloHistory, fetchEloRanking, fetchMatchById } from '../../api/api';
import Spinner from '../Spinner/Spinner';
import {
    formatDateDDMMYY,
    formatFirstLetterOfName,
    renderMatchResult,
    renderPartnerName,
    renderPlayerName
} from '../../utils';

const SMALL_SAMPLE_THRESHOLD = 5; // próg małej próby (zmień na 3, jeśli wolisz)
const SHRINK_K = 5;               // siła „usztywnienia” procenta przy niskim n

// Opcjonalne "usztywnienie" ostatniego % (mieszanka recent i all-time)
const computeShrunkRate = (recentRatePct, recentMatches, allTimeRatePct, k = SHRINK_K) => {
    const r = (recentRatePct ?? 0) / 100;   // 0..1
    const b = (allTimeRatePct ?? 0) / 100;  // 0..1
    if (!recentMatches || recentMatches <= 0) return allTimeRatePct ?? 0;
    const blended = (r * recentMatches + b * k) / (recentMatches + k);
    return blended * 100; // z powrotem do %
};

const EloRanking = () => {
    const [eloData, setEloData] = useState({
        SINGLES_MALE: [],
        SINGLES_FEMALE: [],
        DOUBLES_MALE: [],
        DOUBLES_FEMALE: [],
    });
    const [activeTab, setActiveTab] = useState('SINGLES_MALE');
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUserName, setSelectedUserName] = useState('');
    const [eloHistory, setEloHistory] = useState([]);
    const [matchDetails, setMatchDetails] = useState(null);
    const [highlightedEntryId, setHighlightedEntryId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const savedScrollRef = useRef(0);
    const shouldRestoreScrollRef = useRef(false);

    // pobranie rankingów — 4 osobne wywołania (każdy ranking filtrowany po stronie backendu)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [singlesMale, singlesFemale, doublesMale, doublesFemale] = await Promise.all([
                    fetchEloRanking('SINGLES', 'MALE'),
                    fetchEloRanking('SINGLES', 'FEMALE'),
                    fetchEloRanking('DOUBLES', 'MALE'),
                    fetchEloRanking('DOUBLES', 'FEMALE'),
                ]);

                setEloData({
                    SINGLES_MALE: singlesMale,
                    SINGLES_FEMALE: singlesFemale,
                    DOUBLES_MALE: doublesMale,
                    DOUBLES_FEMALE: doublesFemale,
                });
            } catch (err) {
                console.error(err);
                setError("Błąd pobrania danych");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // historia ELO użytkownika (po kliknięciu karty)
    // gender = 'MALE' | 'FEMALE' — backend zwraca DTO z polem `gender` na każdym wpisie
    // (z tabeli matches), filtrujemy lokalnie żeby modal pokazywał tylko historię z meczów
    // pasującej płci (Singiel M ≠ Singiel K mimo wspólnego endpointu).
    const loadEloHistory = async (userId, matchType, gender) => {
        setLoading(true);
        try {
            const result = await fetchEloHistory(userId, matchType);
            if (result.success) {
                const filtered = gender
                    ? result.data.filter(h => h.gender === gender)
                    : result.data;
                const sortedHistory = filtered.sort((a, b) => b.id - a.id);
                setEloHistory(sortedHistory);
            }
        } catch (err) {
            console.error(err);
            setError("Błąd pobrania historii meczu");
        } finally {
            setLoading(false);
        }
    };

    // szczegóły meczu w modalu — bez przełączania globalnego `loading`, żeby lista historii
    // nie była odmontowywana (modal jest fixed overlay, więc nic nie trzeba ukrywać pod spodem).
    // Dzięki temu po zamknięciu modala scroll listy pozostaje na poprzedniej pozycji.
    const loadMatchDetails = async (matchId) => {
        try {
            const result = await fetchMatchById(matchId);
            if (result.success) {
                setMatchDetails(result.data);
            }
        } catch (err) {
            console.error(err);
            setError("Błąd pobrania danych meczu");
        }
    };

    const handleUserClick = (userId, matchType, name, gender) => {
        savedScrollRef.current = window.scrollY;
        shouldRestoreScrollRef.current = true;
        setSelectedUser({ userId, matchType, gender });
        setSelectedUserName(name);
        loadEloHistory(userId, matchType, gender);
    };

    // Po powrocie z historii (selectedUser → null) przywracamy pozycję scrolla zapamiętaną
    // przy kliknięciu karty, żeby użytkownik wrócił do tego samego gracza, a nie na górę listy.
    useLayoutEffect(() => {
        if (!selectedUser && !loading && shouldRestoreScrollRef.current) {
            window.scrollTo({ top: savedScrollRef.current, left: 0 });
            shouldRestoreScrollRef.current = false;
        }
    }, [selectedUser, loading]);

    const getTabLabels = () => {
        const labels = {
            SINGLES_MALE: 'Singiel M',
            DOUBLES_MALE: 'Debel M',
        };
        if (eloData.SINGLES_FEMALE.length > 0) labels.SINGLES_FEMALE = 'Singiel K';
        if (eloData.DOUBLES_FEMALE.length > 0) labels.DOUBLES_FEMALE = 'Debel K';
        return labels;
    };

    const tabLabels = getTabLabels();

    const handleCloseModal = () => {
        setMatchDetails(null);
        setHighlightedEntryId(null);
    };

    if (loading) return <Spinner size="large" />;

    // Widok historii jednego gracza
    if (selectedUser) {
        return (
            <div className={styles.historyContainer}>
                <button
                    onClick={() => { setSelectedUser(null); setEloHistory([]); setMatchDetails(null); setHighlightedEntryId(null); }}
                    className={styles.backButton}
                >
                    ← Powrót
                </button>

                <h5 className={styles.title}>
                    Historia elo {selectedUser.matchType === 'SINGLES' ? 'singiel' : 'debel'} {selectedUser.gender === 'FEMALE' ? 'K' : 'M'} - {selectedUserName}
                </h5>

                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>ELO</th>
                        <th>+/-</th>
                        <th>Data</th>
                        <th>Oponent</th>
                    </tr>
                    </thead>
                    <tbody>
                    {eloHistory.map((entry, index) => (
                        <tr key={index} className={highlightedEntryId === entry.id ? styles.highlightedRow : ''}>
                            <td>{entry.elo}</td>
                            <td>
                                <a href="#" onClick={(e) => {
                                    e.preventDefault();
                                    setHighlightedEntryId(entry.id);
                                    loadMatchDetails(entry.matchId);
                                }}>
                                    {entry.eloChange}
                                </a>
                            </td>
                            <td>{formatDateDDMMYY(entry.date)}</td>
                            <td>
                                {entry.opponent}<br />
                                {entry.opponentPartner && entry.opponentPartner}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>

                {matchDetails && (
                    <div className={styles.modal}>
                        <div className={styles.modalContent}>
                            <span>{matchDetails.tournamentTitle}</span>
                            <span className={styles.closeButton} onClick={handleCloseModal}>&times;</span>
                            <div className={styles.result}>
                                <div>
                                    <div className={styles.playerNames}>
                                        <div>
                                            {renderPlayerName(
                                                matchDetails.participant1Id,
                                                matchDetails.participant1LastName,
                                                matchDetails.participant1FirstName,
                                                matchDetails
                                            )}
                                            {matchDetails.participant1PartnerLastName && (
                                                <>
                                                    <br />
                                                    <span className={styles.partnerName}>
                            {renderPartnerName(
                                matchDetails.participant1Id,
                                matchDetails.participant1PartnerLastName,
                                matchDetails.participant1PartnerFirstName,
                                matchDetails
                            )}
                          </span>
                                                </>
                                            )}
                                        </div>
                                        <div>
                                            {renderPlayerName(
                                                matchDetails.participant2Id,
                                                matchDetails.participant2LastName,
                                                matchDetails.participant2FirstName,
                                                matchDetails
                                            )}
                                            {matchDetails.participant2PartnerLastName && (
                                                <>
                                                    <br />
                                                    <span className={styles.partnerName}>
                            {renderPartnerName(
                                matchDetails.participant2Id,
                                matchDetails.participant2PartnerLastName,
                                matchDetails.participant2PartnerFirstName,
                                matchDetails
                            )}
                          </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    {matchDetails.setResults && renderMatchResult(matchDetails.setResults)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Widok listy rankingowej
    return (
        <div className={styles.container}>
            {error && <div>Error: {error}</div>}

            <div className={styles.titleRow}>
                <h5 className={styles.title}>Ranking ELO</h5>
                <a href="/elo-description" target="_blank" rel="noreferrer" className={styles.infoButton}>
                    Co to jest ELO?
                </a>
            </div>

            <div className={styles.tabBar}>
                {Object.entries(tabLabels).map(([key, label]) => (
                    <button
                        key={key}
                        className={`${styles.tabButton} ${activeTab === key ? styles.active : ''}`}
                        onClick={() => setActiveTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className={styles.rankingList}>
                {eloData[activeTab].map((user, index) => {
                    const isSmallSample =
                        (user.recentMatches ?? 0) > 0 && (user.recentMatches ?? 0) < SMALL_SAMPLE_THRESHOLD;

                    // surowy % z backendu
                    const recentRateRaw = user.recentWinRate ?? null;

                    // wyświetlana wartość (z „usztywnieniem” przy małej próbie)
                    const recentRateDisplay = recentRateRaw == null
                        ? null
                        : (isSmallSample
                            ? computeShrunkRate(recentRateRaw, user.recentMatches, user.winRate, SHRINK_K)
                            : recentRateRaw);

                    return (
                        <div
                            key={user.userId}
                            className={styles.card}
                            onClick={() =>
                                handleUserClick(
                                    user.userId,
                                    activeTab.includes('SINGLES') ? 'SINGLES' : 'DOUBLES',
                                    `${user.lastName} ${formatFirstLetterOfName(user.firstName)}`,
                                    activeTab.includes('FEMALE') ? 'FEMALE' : 'MALE'
                                )
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                        >
                            <div className={styles.leftSide}>
                <span className={styles.playerInfo}>
                  {index + 1}. {user.lastName} {formatFirstLetterOfName(user.firstName)}
                </span>
                                <div className={styles.eloHero}>
                                    <span className={styles.eloEyebrow}>ELO</span>
                                    <span className={styles.eloValue}>{user.elo}</span>
                                </div>

                                {/* Obszar pasków (stała szerokość) */}
                                <div className={styles.barsContainer}>
                                    {/* 1) Łączna statystyka */}
                                    <div className={styles.barStat}>
                                        <div className={styles.barLabelRow}>
                                            <span className={styles.barLabel}>Wygranych</span>
                                            <span className={styles.barMeta}>
                        {user.winRate.toFixed(1)} %{' '}
                                                <span className={styles.barMuted}>({user.matchesPlayed})</span>
                      </span>
                                        </div>
                                        <div className={styles.bar}>
                                            <div
                                                className={styles.barFill}
                                                style={{
                                                    width: `${Math.max(0, Math.min(100, user.winRate))}%`,
                                                    backgroundColor:
                                                        user.winRate >= 67 ? '#4caf50' : user.winRate >= 34 ? '#ffeb3b' : '#f44336',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* 2) Ostatnie 60 dni */}
                                    <div className={styles.barStat}>
                                        <div className={styles.barLabelRow}>
                      <span className={styles.barLabel}>
                        Ostatnie 60 dni
                          {user.recentEloDelta != null && (user.recentMatches ?? 0) > 0 && (
                              <span
                                  className={
                                      user.recentEloDelta > 0
                                          ? styles.deltaPositive
                                          : user.recentEloDelta < 0
                                              ? styles.deltaNegative
                                              : styles.deltaNeutral
                                  }
                              >
                                  {' ('}
                                  {user.recentEloDelta > 0 ? '+' : ''}{user.recentEloDelta} pkt
                                  {')'}
                              </span>
                          )}
                          {isSmallSample && <span className={styles.badge}>mała próba</span>}
                      </span>
                                            <span className={styles.barMeta}>
                        {recentRateRaw == null ? (
                            <span className={styles.barMuted}>brak danych</span>
                        ) : (
                            <>
                                {recentRateDisplay.toFixed(1)} %{' '}
                                <span className={styles.barMuted}>({user.recentMatches ?? 0})</span>
                            </>
                        )}
                      </span>
                                        </div>
                                        <div className={styles.bar}>
                                            <div
                                                className={`${styles.barFill} ${isSmallSample ? styles.barFillSmallSample : ''}`}
                                                style={{
                                                    width:
                                                        recentRateRaw == null
                                                            ? '0%'
                                                            : `${Math.max(0, Math.min(100, recentRateDisplay))}%`,
                                                    backgroundColor:
                                                        recentRateRaw == null
                                                            ? '#e0e0e0'
                                                            : recentRateDisplay >= 67
                                                                ? '#4caf50'
                                                                : recentRateDisplay >= 34
                                                                    ? '#ffeb3b'
                                                                    : '#f44336',
                                                }}
                                                title={
                                                    isSmallSample
                                                        ? 'Mniej niż 5 meczów w ostatnich 60 dniach — wynik może być niestabilny'
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    </div>

                                    {/* 3) Tiebreaki — łącznie. Pasek pokazujemy tylko jeśli gracz
                                        rozegrał przynajmniej jeden tiebreak. */}
                                    {(user.totalTiebreaks ?? 0) > 0 && (
                                        <div className={styles.barStat}>
                                            <div className={styles.barLabelRow}>
                                                <span className={styles.barLabel}>Tiebreaki</span>
                                                <span className={styles.barMeta}>
                                                    {user.tiebreakWinRate.toFixed(1)} %{' '}
                                                    <span className={styles.barMuted}>({user.totalTiebreaks})</span>
                                                </span>
                                            </div>
                                            <div className={styles.bar}>
                                                <div
                                                    className={styles.barFill}
                                                    style={{
                                                        width: `${Math.max(0, Math.min(100, user.tiebreakWinRate))}%`,
                                                        backgroundColor:
                                                            user.tiebreakWinRate >= 67
                                                                ? '#4caf50'
                                                                : user.tiebreakWinRate >= 34
                                                                    ? '#ffeb3b'
                                                                    : '#f44336',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default EloRanking;
